import React from 'react';
import { createPortal } from 'react-dom';
import styled from '@xstyled/styled-components';
import { select } from '~/lib/select';
import { useTrainingStore } from '../state/useTrainingStore';
import { TrainingState } from '../state/TrainingState';
import { t } from 'ttag';

const Pill = styled.div`
  position: fixed;
  top: 20px;
  right: 70px;
  z-index: hud;
  background: ${p => p.theme.colors.overlayMed};
  padding: 0 10px;
  font-weight: bold;
  height: 35px;
  border-radius: 17.5px;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const selector = (s: TrainingState) =>
  select(
    s,
    'remainingDurationInMinutesForCourseTimers',
    'remainingDurationInSecondsForCourseTimers',
    'shouldDisplayTimeRemainingPill',
    'reviewTimerPaused'
  );

const getHours = (time: number) => Math.floor(time / 60);
const getMinutes = (time: number) => time % 60;

export const TimeRemainingPill: React.FC = () => {
  const {
    remainingDurationInMinutesForCourseTimers: remainingDurationInMinutes,
    remainingDurationInSecondsForCourseTimers: remainingSeconds,
    shouldDisplayTimeRemainingPill,
    reviewTimerPaused
  } = useTrainingStore(selector);

  if (!shouldDisplayTimeRemainingPill) return null;
  if (remainingDurationInMinutes == null && remainingSeconds == null)
    return null;

  const duration = remainingDurationInMinutes ?? 0;
  const hours =
    remainingSeconds == null
      ? getHours(duration)
      : Math.floor(remainingSeconds / 3600);
  const minutes =
    remainingSeconds == null
      ? getMinutes(duration)
      : Math.floor((remainingSeconds % 3600) / 60);
  const seconds = remainingSeconds == null ? null : remainingSeconds % 60;
  const label =
    remainingSeconds === 0
      ? t`Time requirement met`
      : seconds == null
        ? t`Time remaining: ${hours}h ${minutes}m`
        : t`Time remaining: ${hours}h ${minutes}m ${seconds}s`;

  return createPortal(
    <Pill
      className="timeRemainingPillStyleForDarkTheme"
      data-test-id="time-remaining-pill"
    >
      {reviewTimerPaused && remainingSeconds !== 0 ? t`Timer paused` : label}
    </Pill>,
    document.getElementById('root')!
  );
};
